import React from "react"
import Ajax from "../../../Ajax"
import { Button } from "@mui/material"
import { Alert } from "../../.."
import { downloadBase64File, fileFormats } from "../../../misc/download"


const formatPlaceholders = (message, answers) => {
    try {
        if (message) {
            var placeholders = message.matchAll("(?<={).+?(?=})", message)

            placeholders.forEach((placeholder) => {
                var value = answers[placeholder[0]]
                if (value) {
                    message = message.replace(`{${placeholder[0]}}`, value)
                }
            })
        }
    } catch {
        console.log("Error when converting questionnaire placeholders")
    }
    return message
}


const getPageList = (pages, answers, returnSubmittedPages = true) => {

    var pageList = []

    const handlePages = (pages) => {
        pages.forEach(page => {

            var hide = isHidden(page, answers)

            if (!hide) {
                pageList.push(page)
            }

            // Check for conditional pages
            page.fields?.forEach(field => {
                var value = answers[field.name]
                if (field.conditional_pages) {
                    if (value in field.conditional_pages) {
                        handlePages(field.conditional_pages[value])
                    } else if ("else" in field.conditional_pages) {
                        handlePages(field.conditional_pages["else"])
                    }
                }
            })
        })
    }

    handlePages(pages)

    if (!answers.submitted_pages) {
        return returnSubmittedPages ? [] : pageList
    }

    if (returnSubmittedPages) {
        return pageList.filter(page => answers.submitted_pages.includes(page.id))
    }

    return pageList.filter(page => !answers.submitted_pages.includes(page.id))
}


const isHidden = (fieldOrPage, answers) => {

    var valuesToLookFor = {...fieldOrPage.dependsOn, ...fieldOrPage.dependsOnAny, ...fieldOrPage.hideDependsOn, ...fieldOrPage.hideDependsOnAny}

    var fieldsKeysSatisfied = Object.keys(valuesToLookFor).filter((key) => {
        
        var valueToLookFor = valuesToLookFor[key]
        var valueStored = answers[key]
        
        return Array.isArray(valueToLookFor) 
            ? valueToLookFor.includes(valueStored) 
            : valueToLookFor === valueStored
    })

    if (fieldOrPage.dependsOn) {
        return Object.keys(valuesToLookFor).length !== fieldsKeysSatisfied.length
    }

    if (fieldOrPage.hideDependsOn) {
        return !(Object.keys(valuesToLookFor).length !== fieldsKeysSatisfied.length)
    }

    if (fieldOrPage.dependsOnAny) {
        return fieldsKeysSatisfied?.length === 0
    }

    if (fieldOrPage.hideDependsOnAny) {
        return !(fieldsKeysSatisfied?.length === 0)
    }

    return false
}


export default function QuestionnaireAnswers ({questionnaire}) {

    var template = JSON.parse(questionnaire.template.json)
    var answers = JSON.parse(questionnaire.answers)
    var pagesSubmitted = getPageList(template.pages, answers, true)
    var pagesRemaining = getPageList(template.pages, answers, false)

    if (!pagesSubmitted?.length) {
        return <p>This Questionnaire hasn't been started.</p>
    }

    var totalPagesLength = pagesSubmitted.length + pagesRemaining?.length

    return (
        <div>
            <p onClick={() => navigator.clipboard.writeText(questionnaire.answers)}>{pagesSubmitted.length} of {totalPagesLength} pages complete.</p>

            <div className="questionnaire-pages">
                {pagesSubmitted?.map((page, p) => (
                    <div key={p} style={{display: "grid", margin: "5px"}}>
                        <h5>{formatPlaceholders(page.title, answers)}</h5>

                        {page.fields?.map((field, f) => <QuestionnaireField key={f} field={field} answers={answers} uploads={questionnaire.uploads} />)}

                        {page.table ? answers[page.table?.list_name]?.map((tableEntryAnswers, i) => (
                            <div key={i} style={{margin: "2px 0"}}>
                                {i > 0 ? <div style={{height: "0.5px", width: "auto", margin: "5px 10px", backgroundColor: "grey"}}></div> : null}

                                {page.table.fields?.map((field, f) => <QuestionnaireField key={f} field={field} answers={{...answers, ...tableEntryAnswers}} uploads={questionnaire.uploads} />)}
                            </div>
                        )) : null}
                    </div>   
                ))}
            </div>
        </div>
    )
}

const getFieldValue = (field, answers) => {
    var value = answers[field.name]

    if (field.options?.hasOwnProperty(value)) {
        value = field.options[value]
    }

    if (field.type === "checkbox") {
        value = value === "true" ? "Yes" : "No"
    }

    return value
}

function QuestionnaireField ({field, answers, uploads}) {

    if (!field.name || isHidden(field, answers)) {
        return <React.Fragment key={JSON.stringify(field)}></React.Fragment>
    }

    var value = getFieldValue(field, answers)
    value = formatPlaceholders(value, answers)
    field.label = formatPlaceholders(field.label, answers)

    if (field.type === "file") {
        var upload = uploads.find(u => u.uid === value)
        return <DownloadField field={field} upload={upload} />
    }
    
    return (
        <div style={{display: "grid", gridTemplateColumns: "60% 40%", gap: "10px", margin: "5px 0 0 14px", overflowWrap: "anywhere"}}>
            <small dangerouslySetInnerHTML={{ __html: field.label }}></small>

            <small>{value}</small>
        </div>
    )
}


function DownloadField ({field, upload}) {
    
    const [downloading, setDownloading] = React.useState(false)

    const download = async () => {
        setDownloading(true)
        
        await Ajax.Questionnaire.GetUpload(upload.uid).then((response) => {
            var mimeType = fileFormats[upload.fileFormat?.toLowerCase()]
            if (mimeType) {
                downloadBase64File(upload.displayName, response.data.data, mimeType)
            }
        }).catch((response) => {
            Alert({message: response.data?.detail ? response.data.detail : "Download failed"})
        })

        setDownloading(false)
    }

    return (
        <div style={{display: "grid", gridTemplateColumns: "50% auto 100px", margin: "5px 0 0 14px"}}>
            <small>{field.label}</small>
            <small>{upload?.displayName}</small>
            <small>
                {upload ? (
                    <Button 
                        onClick={() => download()}
                        className="btn btn-xs" 
                        color="success" 
                        variant="contained"
                        style={{marginLeft: "auto"}}>
                        Download
                    </Button>
                ) : "File unavailable"}
            </small>
        </div>
    )    
}